import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { apiClient } from '@/shared/services/api';

type FeeTier = 'split_fee' | 'hybrid' | 'absorbed';

interface Invoice {
  id: string;
  invoice_number: string;
  customer_name: string;
  amount_cents: number;
  platform_fee_cents: number; // Legacy field
  currency: string;
  status: string;
  title: string | null;
  notes: string | null;
  fwd_payment_intent_id: string | null;
  fwd_client_secret: string | null;
  merchant_id: string;
  // New fee tier fields
  fee_tier?: FeeTier;
  technology_fee_cents?: number;
  merchant_fee_cents?: number;
  customer_total_cents?: number;
  merchant_receives_cents?: number;
  platform_revenue_cents?: number;
  surcharge_applied?: boolean;
  payment_method_type?: string;
  card_brand?: string;
  is_debit?: boolean;
  metadata?: {
    is_dual_pricing?: boolean;
    cash_price_cents?: number;
    card_price_cents?: number;
    [key: string]: any;
  };
}

// Extend Window to include Forward types
declare global {
  interface Window {
    Forward?: any;
  }
}

const FWD_PUBLISHABLE_KEY = import.meta.env.VITE_FWD_PUBLISHABLE_KEY;

export default function PaymentPage() {
  const { invoice_id } = useParams<{ invoice_id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'failed'>('pending');
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [paymentElement, setPaymentElement] = useState<any>(null);
  const [tokenizeFunction, setTokenizeFunction] = useState<(() => Promise<any>) | null>(null);
  const [isFormReady, setIsFormReady] = useState(false);
  const [retryCount] = useState(0);
  const [sdkLoadAttempts, setSdkLoadAttempts] = useState(0);
  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef(false);

  // Amount finalization state (for debit card detection)
  const [amountAdjustmentMessage, setAmountAdjustmentMessage] = useState<string | null>(null);
  const [finalAmountCents, setFinalAmountCents] = useState<number | null>(null);
  const [isProcessingAdjustment, setIsProcessingAdjustment] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'bank' | 'card' | 'debit'>('card');
  const [methodConfirmed, setMethodConfirmed] = useState(false); // Don't load Forward element until method is chosen

  // Fee config from backend
  const [feeConfig, setFeeConfig] = useState({ technology_fee_cents: 150, merchant_fee_percent: 2.75 });

  useEffect(() => {
    apiClient.get<{ technology_fee_cents: number; merchant_fee_percent: number }>('/payments/fee-config')
      .then(setFeeConfig)
      .catch(() => {}); // Use defaults on error
  }, []);

  // Load Forward SDK with retry logic
  useEffect(() => {
    const loadSDK = () => {
      // Check if SDK is already loaded
      if (window.Forward) {
        setSdkLoaded(true);
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="forward.js"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.src = 'https://sandbox-cdn.pci.getfwd.com/sdk/forward.js';
      script.async = true;
      script.onload = () => {
        console.log('Forward SDK loaded successfully');
        setSdkLoaded(true);
        setSdkLoadAttempts(0);
      };
      script.onerror = () => {
        console.error('Failed to load Forward SDK, attempt:', sdkLoadAttempts + 1);

        if (sdkLoadAttempts < 2) {
          // Retry up to 3 times (0, 1, 2)
          setSdkLoadAttempts(prev => prev + 1);
          setTimeout(() => {
            loadSDK();
          }, 2000); // Wait 2 seconds before retry
        } else {
          setError('Unable to load payment system. Please refresh the page or try again later.');
        }
      };
      document.head.appendChild(script);
    };

    loadSDK();

    return () => {
      // Cleanup script on unmount
      const script = document.querySelector('script[src*="forward.js"]');
      if (script && document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [sdkLoadAttempts]);

  // Load invoice
  useEffect(() => {
    if (invoice_id) {
      loadInvoice();
    }
  }, [invoice_id]);

  // Initialize Forward Payment Element only after customer has confirmed their payment method
  useEffect(() => {
    if (sdkLoaded && invoice && invoice.fwd_client_secret && methodConfirmed) {
      initializeForwardPayment();
    }
  }, [sdkLoaded, invoice, selectedPaymentMethod, methodConfirmed]);

  const loadInvoice = async () => {
    if (!invoice_id) {
      setError('Invalid payment link');
      setLoading(false);
      return;
    }

    try {
      // SECURITY: Use backend API instead of direct database query
      const response = await apiClient.get<{ ok: boolean; invoice: any }>(`/invoices/${invoice_id}`);

      if (!response.ok || !response.invoice) {
        setError('Invoice not found');
        setLoading(false);
        return;
      }

      const invoiceData = response.invoice as Invoice;
      setInvoice(invoiceData);

      // Check if already paid
      if (invoiceData.status === 'PAID') {
        setPaymentStatus('success');
        setLoading(false);
        return;
      }

      // Check if we have Forward payment intent
      if (!invoiceData.fwd_client_secret) {
        setError('Payment not configured. Please contact the merchant.');
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load payment details');
      setLoading(false);
    }
  };

  const initializeForwardPayment = async () => {
    if (!window.Forward) {
      console.error('Forward SDK not loaded');
      setError('Payment system not available');
      return;
    }

    if (!invoice?.fwd_client_secret) {
      console.error('No client secret available');
      setError('Payment not configured');
      return;
    }

    if (!FWD_PUBLISHABLE_KEY) {
      console.error('Forward publishable key not configured');
      setError('Payment system configuration error');
      return;
    }

    try {
      // Unmount existing payment element if any
      if (paymentElement) {
        try {
          paymentElement.unmount();
        } catch (e) {
          console.log('Payment element unmount error (safe to ignore):', e);
        }
        setPaymentElement(null);
        setTokenizeFunction(null);
        setIsFormReady(false);
      }

      console.log('Initializing Forward Payment Element...');

      // Use the appropriate client secret based on selected payment method.
      // Both dual pricing (restricted states) and split_fee now have separate card/ACH intents.
      const metadata = invoice.metadata || {};
      const isDualPricing = metadata.is_dual_pricing;
      const hasTwoIntents = isDualPricing || invoice.fee_tier === 'split_fee';

      let clientSecret = invoice.fwd_client_secret;

      if (hasTwoIntents) {
        // ACH/bank → ACH intent (bank token, no surcharge)
        // card/debit → card intent (card token; debit price adjusted server-side via PATCH)
        if (selectedPaymentMethod === 'bank' && metadata.ach_client_secret) {
          clientSecret = metadata.ach_client_secret;
          console.log('Using ACH payment intent for bank transfer');
        } else if ((selectedPaymentMethod === 'card' || selectedPaymentMethod === 'debit') && metadata.card_client_secret) {
          clientSecret = metadata.card_client_secret;
          console.log('Using Card payment intent (method:', selectedPaymentMethod, ')');
        }
      }

      // Use the correct SDK constructor per payment method:
      // - card/debit → createCardElement (card form only, no bank fields)
      // - bank       → createBankElement (bank/ACH form only, no card fields)
      let element: any;
      if (selectedPaymentMethod === 'bank') {
        element = await window.Forward.createBankElement({
          apiKey: FWD_PUBLISHABLE_KEY,
          clientSecret: clientSecret,
        });
      } else {
        // 'card' and 'debit' both use CardElement — debit detection happens post-tokenization
        element = await window.Forward.createCardElement({
          apiKey: FWD_PUBLISHABLE_KEY,
          clientSecret: clientSecret,
        });
      }

      setPaymentElement(element);

      // Shared style for all elements
      const elementStyle = {
        borderRadius: '8px',
        borderColor: '#e5e7eb',
        focusBorderColor: '#3b82f6',
        textColor: '#111827',
        backgroundColor: '#ffffff',
        inputBackgroundColor: '#ffffff',
      };

      // Mount to DOM
      if (paymentContainerRef.current) {
        if (selectedPaymentMethod === 'bank') {
          // BankElement: no onChange/onReady/onCancel — onSuccess fires directly with token
          element.mount(paymentContainerRef.current, {
            onSuccess: async (result: any) => {
              try {
                const paymentMethodId = result?.token || result?.payment_method_id || result?.id;
                if (!paymentMethodId) {
                  setPaymentStatus('failed');
                  setError('Payment tokenization failed - missing payment method ID');
                  return;
                }
                setIsProcessingAdjustment(true);
                setAmountAdjustmentMessage(null);
                const finalizeResponse = await apiClient.post<{
                  ok: boolean; final_amount_cents: number; original_amount_cents: number;
                  adjusted: boolean; message: string; error?: string;
                }>('/payments/finalize-invoice-amount', {
                  invoice_id,
                  payment_method_id: paymentMethodId,
                  postal_code: null,
                  client_secret: clientSecret,
                });
                if (!finalizeResponse.ok || finalizeResponse.error) {
                  throw new Error(finalizeResponse.error || 'Failed to finalize amount');
                }
                setFinalAmountCents(finalizeResponse.final_amount_cents);
                setIsProcessingAdjustment(false);
                const response = await apiClient.post<{ ok: boolean; invoice: any; error?: string; }>(
                  '/payments/process-forward-payment',
                  { invoice_id, payment_method_id: paymentMethodId, selected_payment_method: 'bank', client_secret: clientSecret }
                );
                if (!response.ok || response.error) {
                  setPaymentStatus('failed');
                  setError(response.error || 'Payment processing failed');
                  return;
                }
                setPaymentStatus('success');
                setTimeout(() => loadInvoice(), 2000);
              } catch (err: any) {
                setPaymentStatus('failed');
                setError(err?.message || 'Failed to process payment');
                setIsProcessingAdjustment(false);
              }
            },
            onError: (error: any) => {
              console.error('Bank payment error:', error);
              setPaymentStatus('failed');
              setError(typeof error === 'string' ? error : error?.message || 'Payment failed. Please try again.');
            },
            style: elementStyle,
          });
          // BankElement handles its own submit flow — mark form ready immediately
          setIsFormReady(true);
          console.log('Bank element mounted successfully');
        } else {
          // CardElement: has onChange/onReady/onCancel flow
          element.mount(paymentContainerRef.current, {
            onChange: (event: any) => {
              console.log('Payment form changed:', event);
              setIsFormReady(event.ready);
              if (!event.ready && event.errors) {
                console.log('Form errors:', event.errors);
              }
            },
            onReady: (tokenize: any) => {
              console.log('Payment element ready - form is valid');
              setIsFormReady(true);
              setTokenizeFunction(() => tokenize);
            },
            onSuccess: async (result: any) => {
              // CRITICAL: Wrap entire callback in try-catch to prevent silent failures
              try {
                console.log('[PAYMENT] ==================== onSuccess START ====================');
              console.log('[PAYMENT] onSuccess called with result:', result);
              console.log('[PAYMENT] Result keys:', Object.keys(result || {}));
              console.log('[PAYMENT] Result.token:', result?.token);
              console.log('[PAYMENT] Result.payment_method_id:', result?.payment_method_id);
              console.log('[PAYMENT] Result.id:', result?.id);

              // Extract payment method ID from result
              const paymentMethodId = result?.token || result?.payment_method_id || result?.id;

              console.log('[PAYMENT] Extracted paymentMethodId:', paymentMethodId);

              if (!paymentMethodId) {
                console.error('[PAYMENT] ❌ No payment method ID in result:', result);
                setPaymentStatus('failed');
                setError('Payment tokenization failed - missing payment method ID');
                return Promise.reject(new Error('Missing payment method ID'));
              }

              console.log('[PAYMENT] ✅ Payment method ID extracted:', paymentMethodId);
              console.log('[PAYMENT] invoice_id:', invoice_id);
              console.log('[PAYMENT] Setting processing state...');

              setIsProcessingAdjustment(true);
              setAmountAdjustmentMessage(null);

              console.log('[PAYMENT] Step 1: Finalizing amount with card type detection...');
              console.log('[PAYMENT] About to call apiClient.post...');
              console.log('[PAYMENT] Endpoint: /payments/finalize-invoice-amount');
              console.log('[PAYMENT] Payload:', {
                invoice_id,
                payment_method_id: paymentMethodId,
                postal_code: null
              });

              // Step 1: Call backend to finalize amount based on card type (CRITICAL for debit detection)
              console.log('[PAYMENT] Making API call NOW...');
              const finalizeResponse = await apiClient.post<{
                ok: boolean;
                final_amount_cents: number;
                original_amount_cents: number;
                adjusted: boolean;
                message: string;
                card_type: string;
                is_debit: boolean;
                payment_method_id: string;
                error?: string;
              }>('/payments/finalize-invoice-amount', {
                invoice_id: invoice_id,
                payment_method_id: paymentMethodId,
                postal_code: null, // Could get from user input if needed
                client_secret: clientSecret,
              });

              console.log('[PAYMENT] ✅ Finalize API call completed! Response:', finalizeResponse);

              if (!finalizeResponse.ok || finalizeResponse.error) {
                console.error('[PAYMENT] ❌ Finalize response has error:', finalizeResponse.error);
                throw new Error(finalizeResponse.error || 'Failed to finalize amount');
              }

              console.log('[PAYMENT] Amount finalized successfully:', finalizeResponse);

              // Step 2: Show adjustment message if amount changed (debit card detected)
              if (finalizeResponse.adjusted) {
                setAmountAdjustmentMessage(finalizeResponse.message);
                setFinalAmountCents(finalizeResponse.final_amount_cents);

                console.log('[PAYMENT] Amount adjusted:', {
                  from: finalizeResponse.original_amount_cents,
                  to: finalizeResponse.final_amount_cents,
                  reason: finalizeResponse.message
                });

                // Give user 2 seconds to see the adjustment message
                await new Promise(resolve => setTimeout(resolve, 2000));
              } else {
                setFinalAmountCents(finalizeResponse.final_amount_cents);
              }

              setIsProcessingAdjustment(false);

              console.log('[PAYMENT] Step 2: Processing payment with finalized amount...');
              console.log('[PAYMENT] Calling /payments/process-forward-payment');
              console.log('[PAYMENT] Payload:', {
                invoice_id,
                payment_method_id: paymentMethodId,
                selected_payment_method: selectedPaymentMethod,
              });

              // Step 3: Call backend to process payment with finalized amount
              // Debit uses the card intent (card token) — keep as 'card'
              // Only actual 'bank' (ACH) uses the ACH intent
              const intentMethod = selectedPaymentMethod === 'debit' ? 'card' : selectedPaymentMethod;
              const response = await apiClient.post<{
                ok: boolean;
                invoice: any;
                error?: string;
              }>('/payments/process-forward-payment', {
                invoice_id: invoice_id,
                payment_method_id: paymentMethodId,
                selected_payment_method: intentMethod,
                client_secret: clientSecret,
              });

              console.log('[PAYMENT] ✅ Process payment API call completed! Response:', response);

              if (!response.ok || response.error) {
                console.error('[PAYMENT] ❌ Process payment response has error:', response.error);
                setPaymentStatus('failed');
                setError(response.error || 'Payment processing failed');
                return Promise.reject(new Error(response.error || 'Payment failed'));
              }

              console.log('[PAYMENT] ✅✅✅ Payment processed successfully!');
              setPaymentStatus('success');

              // Show final success message
              if (finalizeResponse.adjusted) {
                setAmountAdjustmentMessage(`✓ Payment successful! Charged $${(finalizeResponse.final_amount_cents / 100).toFixed(2)}`);
              }

              // Reload invoice to get updated status
              console.log('[PAYMENT] Scheduling invoice reload...');
              setTimeout(() => loadInvoice(), 2000);

              console.log('[PAYMENT] ==================== onSuccess END (SUCCESS) ====================');
              return Promise.resolve();
            } catch (err: any) {
              console.error('[PAYMENT] ==================== onSuccess CAUGHT ERROR ====================');
              console.error('[PAYMENT] Error type:', err?.constructor?.name);
              console.error('[PAYMENT] Error message:', err?.message);
              console.error('[PAYMENT] Error stack:', err?.stack);
              console.error('[PAYMENT] Full error object:', err);
              console.error('[PAYMENT] ==================== END ERROR ====================');

              setPaymentStatus('failed');
              setError(err?.message || 'Failed to process payment');
              setIsProcessingAdjustment(false);
              return Promise.reject(err);
            }
          },
            onError: (error: any) => {
              console.error('Payment error:', error);
              setPaymentStatus('failed');
              setError(error?.message || 'Payment failed. Please try again.');
            },
            onCancel: () => {
              console.log('User cancelled Apple Pay or Google Pay');
              setPaymentStatus('pending');
            },
            style: elementStyle,
          });
          console.log('Card element mounted successfully');
        }
      }
    } catch (err: any) {
      console.error('Error initializing Forward payment:', err);
      setError(err?.message || 'Failed to initialize payment');
    }
  };

  const handlePayNowClick = async () => {
    if (!tokenizeFunction || !isFormReady) {
      setError('Please complete the payment form');
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setPaymentStatus('processing');
      setError(null);

      // Call the tokenize function provided by onReady
      await tokenizeFunction();
      // onSuccess callback will handle the rest
    } catch (err: any) {
      console.error('Tokenization error:', err);
      setPaymentStatus('failed');
      isSubmittingRef.current = false;

      // Provide more helpful error messages
      let errorMessage = 'Payment failed. Please try again.';
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.code === 'card_declined') {
        errorMessage = 'Your card was declined. Please try a different payment method.';
      } else if (err?.code === 'insufficient_funds') {
        errorMessage = 'Insufficient funds. Please try a different card.';
      } else if (err?.code === 'network_error') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    }
  };

  // When bank (ACH) is selected for a split_fee invoice, update customer_total_cents in DB.
  // Debit price is adjusted server-side at finalize time (after card tokenization detects debit).
  const handleMethodSelect = async (method: 'card' | 'debit' | 'bank') => {
    setSelectedPaymentMethod(method);
    // Only for actual bank/ACH selection — update DB to reflect cash price
    if (!isDualPricing && invoice?.fee_tier === 'split_fee' && method === 'bank') {
      try {
        await apiClient.post('/payments/update-payment-amount', {
          invoice_id: invoice_id,
          payment_method_type: 'bank',
          client_secret: invoice?.fwd_client_secret,
        });
      } catch {
        // Non-fatal — display still shows correct cashPrice
      }
    }
    setMethodConfirmed(true);
  };

  const handleRetryPayment = () => {
    setPaymentStatus('pending');
    setError(null);
    isSubmittingRef.current = false;
    setIsFormReady(false);

    // Clear and reinitialize the payment element
    if (paymentElement) {
      setPaymentElement(null);
      setTokenizeFunction(null);
      // The useEffect will reinitialize it
    }
  };

  // Calculate total amount based on fee tier
  const getTotalAmount = () => {
    if (!invoice) return 0;
    // Use finalized amount if available (after debit card detection)
    if (finalAmountCents !== null) {
      return finalAmountCents;
    }
    // Use customer_total_cents if available (new system)
    if (invoice.customer_total_cents !== undefined) {
      return invoice.customer_total_cents;
    }
    // Fallback to legacy platform_fee_cents
    return invoice.amount_cents + (invoice.platform_fee_cents || 0);
  };

  // Check if dual pricing is enabled
  const isDualPricing = invoice?.metadata?.is_dual_pricing || false;

  // cashPrice = what debit/bank pays (no merchant surcharge):
  //   split_fee:  amount + tech fee (merchant fee waived for debit/bank)
  //   hybrid:     amount + tech fee (customer always pays tech fee, merchant fee billed monthly)
  //   absorbed:   amount only (merchant absorbs everything — customer pays nothing extra)
  const computeCashPrice = () => {
    if (!invoice) return 0;
    if (invoice?.metadata?.cash_price_cents) return invoice.metadata.cash_price_cents;
    const tier = invoice.fee_tier;
    if (tier === 'absorbed') return invoice.amount_cents;
    // split_fee and hybrid: amount + tech fee
    return invoice.amount_cents + (invoice.technology_fee_cents || feeConfig.technology_fee_cents);
  };
  const cashPrice = computeCashPrice();
  const cardPrice = invoice?.metadata?.card_price_cents || getTotalAmount();


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Invoice details skeleton */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-px w-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            </div>

            {/* Payment form skeleton - shows what's loading */}
            <div className="space-y-3">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4 space-y-3">
                <div className="animate-pulse">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-600">Loading secure payment options...</span>
                  </div>

                  {/* Apple Pay button placeholder */}
                  <div className="bg-black/5 rounded-md h-12 mb-3" />

                  {/* Card input placeholders */}
                  <div className="space-y-3">
                    <div className="bg-gray-100 rounded-md h-10" />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-100 rounded-md h-10" />
                      <div className="bg-gray-100 rounded-md h-10" />
                    </div>
                    <div className="bg-gray-100 rounded-md h-10" />
                  </div>
                </div>
              </div>
              <Skeleton className="h-12 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              Payment Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'success' || invoice?.status === 'PAID') {
    const totalAmount = getTotalAmount();

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6" />
              Payment Successful
            </CardTitle>
            <CardDescription>Your payment has been processed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                Thank you for your payment! You will receive a confirmation shortly.
              </p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice:</span>
                <span className="font-medium">{invoice?.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-medium">
                  ${(totalAmount / 100).toFixed(2)} {invoice?.currency}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invoiceAmount = invoice?.amount_cents || 0;
  const platformFee = invoice?.platform_fee_cents || 0;
  const totalAmount = getTotalAmount();
  // For dual pricing, reflect the selected payment method in the displayed total
  const displayTotal = isDualPricing
    ? ((selectedPaymentMethod === 'bank' || selectedPaymentMethod === 'debit') ? cashPrice : cardPrice)
    : (selectedPaymentMethod === 'bank' || selectedPaymentMethod === 'debit') ? cashPrice : totalAmount;

  // Pre-payment method selector screen — shown before the Forward payment form loads
  if (!methodConfirmed && invoice && paymentStatus === 'pending') {
    const noFeePrice = cashPrice; // debit / bank price
    const creditCardPrice = isDualPricing ? cardPrice : totalAmount;
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/wlkthruicon.png" alt="Wlkthru" className="h-10 w-10 object-contain" />
          </div>

          {/* Invoice summary */}
          <div className="text-center mb-6">
            {invoice.customer_name && (
              <p className="text-base font-semibold text-gray-900">{invoice.customer_name}</p>
            )}
            {invoice.title && (
              <p className="text-sm text-gray-500 mt-0.5">{invoice.title}</p>
            )}
            {invoice.notes && (
              <p className="text-xs text-gray-400 mt-0.5">{invoice.notes}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">#{invoice.invoice_number}</p>
          </div>

          <p className="text-xs text-gray-400 text-center mb-4 uppercase tracking-wide">Select payment method</p>

          <div className="space-y-2">
            {/* Credit Card */}
            <button
              onClick={() => handleMethodSelect('card')}
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Credit Card</p>
                  <p className="text-xs text-gray-500">Processing fee applies</p>
                </div>
                <p className="text-base font-bold text-gray-900">${(creditCardPrice / 100).toFixed(2)}</p>
              </div>
            </button>

            {/* Debit Card — only shown for non-dual-pricing (debit detection happens post-tokenization)
                For dual pricing (restricted states), debit goes through the Bank/ACH path instead */}
            {!isDualPricing && (
              <button
                onClick={() => handleMethodSelect('debit')}
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Debit Card</p>
                    <p className="text-xs text-gray-500">No surcharge on debit</p>
                  </div>
                  <p className="text-base font-bold text-gray-900">${(noFeePrice / 100).toFixed(2)}</p>
                </div>
              </button>
            )}

            {/* Bank / ACH */}
            <button
              onClick={() => handleMethodSelect('bank')}
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Bank / ACH</p>
                  <p className="text-xs text-gray-500">Direct bank transfer</p>
                </div>
                <p className="text-base font-bold text-gray-900">${(noFeePrice / 100).toFixed(2)}</p>
              </div>
            </button>
          </div>

          <p className="text-xs text-gray-300 text-center mt-6">Secure payment · Powered by Wlkthru</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Pay Invoice</CardTitle>
          <CardDescription>Invoice #{invoice?.invoice_number}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invoice Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Bill to</p>
                <p className="font-medium">{invoice?.customer_name}</p>
              </div>
            </div>

            {invoice?.title && (
              <div>
                <p className="text-sm text-gray-600">Description</p>
                <p className="font-medium">{invoice.title}</p>
              </div>
            )}

            {invoice?.notes && (
              <div>
                <p className="text-sm text-gray-600">Notes</p>
                <p className="text-sm">{invoice.notes}</p>
              </div>
            )}

            {/* Amount Breakdown */}
            <div className="border-t pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Invoice Amount:</span>
                <span className="font-medium">
                  ${(invoiceAmount / 100).toFixed(2)}
                </span>
              </div>

              {/* Display fees based on fee tier */}
              {invoice?.fee_tier === 'split_fee' && (
                <>
                  {invoice.technology_fee_cents && invoice.technology_fee_cents > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Technology Service Fee:</span>
                      <span className="font-medium">
                        ${(invoice.technology_fee_cents / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Hide merchant fee row for ACH/bank selection (dual pricing) or after debit detection */}
                  {invoice.merchant_fee_cents && invoice.merchant_fee_cents > 0
                    && !(selectedPaymentMethod === 'bank' || selectedPaymentMethod === 'debit')
                    && !(finalAmountCents !== null && finalAmountCents <= cashPrice) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Merchant Fee ({feeConfig.merchant_fee_percent}%):</span>
                      <span className="font-medium">
                        ${(invoice.merchant_fee_cents / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {invoice?.fee_tier === 'hybrid' && invoice.technology_fee_cents && invoice.technology_fee_cents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Technology Service Fee:</span>
                  <span className="font-medium">
                    ${(invoice.technology_fee_cents / 100).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Legacy platform fee (for old invoices) */}
              {!invoice?.fee_tier && platformFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Platform Fee:</span>
                  <span className="font-medium">
                    ${(platformFee / 100).toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span className="text-blue-600">
                  ${(displayTotal / 100).toFixed(2)} {invoice?.currency}
                </span>
              </div>

              {/* Fee disclosure for credit card surcharge */}
              {invoice?.fee_tier === 'split_fee' && (
                <div className="text-xs text-gray-500 pt-2">
                  {(selectedPaymentMethod === 'bank' || selectedPaymentMethod === 'debit') ? (
                    <p>The Technology Service Fee (${(feeConfig.technology_fee_cents / 100).toFixed(2)}) is charged by Wlkthru for software services. No merchant fee applies to {selectedPaymentMethod === 'debit' ? 'debit card' : 'ACH'} payments.</p>
                  ) : (
                    <p>The Technology Service Fee (${(feeConfig.technology_fee_cents / 100).toFixed(2)}) is charged by Wlkthru for software services. The Merchant Fee ({feeConfig.merchant_fee_percent}%) is a credit card surcharge and is not applied to Debit cards or ACH payments.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected payment method indicator */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {selectedPaymentMethod === 'bank' ? 'Bank / ACH' : selectedPaymentMethod === 'debit' ? 'Debit Card' : 'Credit Card'}
              </p>
              <p className="text-xs text-gray-500">
                {selectedPaymentMethod === 'card' ? 'Processing fee included' : 'No surcharge'}
              </p>
            </div>
            <button
              onClick={() => { setMethodConfirmed(false); setPaymentElement(null); setTokenizeFunction(null); setIsFormReady(false); }}
              className="text-xs text-gray-500 hover:text-gray-800 underline"
            >
              Change
            </button>
          </div>

          {/* Error Alert */}
          {error && paymentStatus === 'failed' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryPayment}
                  className="ml-4 bg-white hover:bg-gray-100"
                >
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Amount Adjustment Message (Debit Card Detection) */}
          {amountAdjustmentMessage && (
            <div className={`p-4 rounded-lg border-2 ${
              amountAdjustmentMessage.includes('Debit')
                ? 'bg-green-50 border-green-300'
                : amountAdjustmentMessage.includes('✓')
                ? 'bg-green-50 border-green-300'
                : 'bg-blue-50 border-blue-300'
            }`}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {amountAdjustmentMessage.includes('✓') ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    amountAdjustmentMessage.includes('Debit')
                      ? 'text-green-800'
                      : amountAdjustmentMessage.includes('✓')
                      ? 'text-green-800'
                      : 'text-blue-800'
                  }`}>
                    {amountAdjustmentMessage}
                  </p>
                  {finalAmountCents && invoice && finalAmountCents !== invoice.customer_total_cents && (
                    <p className="text-xs text-gray-600 mt-1">
                      Original amount: ${(invoice.customer_total_cents / 100).toFixed(2)} →
                      Final amount: ${(finalAmountCents / 100).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Processing Adjustment Overlay */}
          {isProcessingAdjustment && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-center text-gray-700 font-medium">
                  Verifying payment method...
                </p>
                <p className="text-center text-sm text-gray-500 mt-1">
                  Checking for applicable fees
                </p>
              </div>
            </div>
          )}

          {/* Forward Payment Element Container */}
          <div className="relative">
            {!sdkLoaded && (
              <div className="border-2 border-gray-200 rounded-lg p-8 bg-white">
                <div className="flex flex-col items-center gap-3 mb-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600 font-medium">Loading secure payment options...</p>
                </div>

                {/* Show what's loading */}
                <div className="space-y-3 animate-pulse">
                  {/* Apple Pay placeholder */}
                  <div className="bg-black/5 rounded-md h-12 flex items-center justify-center">
                    <span className="text-xs text-gray-400">Apple Pay</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400">or pay with card</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Card input placeholders */}
                  <div className="space-y-2">
                    <div className="bg-gray-100 rounded-md h-10" />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-100 rounded-md h-10" />
                      <div className="bg-gray-100 rounded-md h-10" />
                    </div>
                    <div className="bg-gray-100 rounded-md h-10" />
                  </div>
                </div>
              </div>
            )}

            {sdkLoaded && invoice?.fwd_client_secret && (
              <>
                {paymentStatus === 'processing' && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                      <p className="text-sm text-gray-600 font-medium">Processing payment...</p>
                      <p className="text-xs text-gray-500">Please do not close this page</p>
                    </div>
                  </div>
                )}

                {/* Forward Payment Element mounts here - includes Apple Pay + Card Element */}
                <div
                  ref={paymentContainerRef}
                  className="min-h-[300px] rounded-lg"
                />
              </>
            )}

            {sdkLoaded && !invoice?.fwd_client_secret && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Payment not configured. Please contact the merchant.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Pay Now Button */}
          {sdkLoaded && invoice?.fwd_client_secret && (
            <Button
              onClick={handlePayNowClick}
              disabled={!isFormReady || paymentStatus === 'processing'}
              className="w-full"
              size="lg"
            >
              {paymentStatus === 'processing' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay $${((finalAmountCents ?? displayTotal) / 100).toFixed(2)} ${invoice?.currency}`
              )}
            </Button>
          )}

          {/* Security Notice */}
          <div className="text-center">
            <p className="text-xs text-gray-500">
              🔒 Secure payment powered by Forward
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
